import React, { useEffect, useState, useMemo } from "react";
import { api } from "../api/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./QpscCups.css";

export default function QpscCups() {

  const { user } = useAuth();
  const navigate = useNavigate();

  const [cups, setCups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchFirst, setSearchFirst] = useState("");
  const [searchLast, setSearchLast] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [sortBy, setSortBy] = useState("caps");
  const [recalcLoading, setRecalcLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/cups"),
      api.get("/users")
    ]).then(([cupsRes, usersRes]) => {
      setCups(cupsRes || []);
      setUsers(usersRes || []);
      setLoading(false);
    });
  }, []);

  const recalculateCups = async () => {
    try {
      setRecalcLoading(true);

      await api.post("/cups/recalculate");

      const [cupsRes, usersRes] = await Promise.all([
        api.get("/cups"),
        api.get("/users")
      ]);

      setCups(cupsRes || []);
      setUsers(usersRes || []);

    } catch (err) {
      console.error("Recalculate error:", err);
    } finally {
      setRecalcLoading(false);
    }
  };

  const enriched = useMemo(() => {
    return cups.map(c => {
      const u = users.find(x => x.id === c.playerId);
      return {
        ...c,
        firstName: u?.firstName || "",
        lastName: u?.lastName || "",
        dob: u?.dob || "",
        gender: u?.gender || ""
      };
    });
  }, [cups, users]);

  const filtered = useMemo(() => {
    return enriched.filter(p => {

      if (searchFirst &&
        !p.firstName.toLowerCase().includes(searchFirst.toLowerCase()))
        return false;

      if (searchLast &&
        !p.lastName.toLowerCase().includes(searchLast.toLowerCase()))
        return false;

      if (genderFilter && p.gender !== genderFilter)
        return false;

      if (seasonFilter &&
        (!p.appearancesBySeason ||
         !p.appearancesBySeason[seasonFilter]))
        return false;

      return true;
    });
  }, [enriched, searchFirst, searchLast, genderFilter, seasonFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];

    switch (sortBy) {
      case "goals":
        copy.sort((a,b)=> b.totalGoals - a.totalGoals);
        break;
      case "minutes":
        copy.sort((a,b)=> b.totalMinutes - a.totalMinutes);
        break;
      case "cards":
        copy.sort((a,b)=>
          (b.totalYellowCards + b.totalRedCards) -
          (a.totalYellowCards + a.totalRedCards)
        );
        break;
      default:
        copy.sort((a,b)=> b.totalAppearances - a.totalAppearances);
    }

    return copy;
  }, [filtered, sortBy]);

  const seasons = useMemo(() => {
    const set = new Set();
    enriched.forEach(p => {
      Object.keys(p.appearancesBySeason || {})
        .forEach(s => set.add(s));
    });
    return Array.from(set).sort().reverse();
  }, [enriched]);


    const isSuperAdmin =
      Array.isArray(user?.contexts) &&
      user.contexts.some(
        c =>
          String(c.role).toLowerCase() === "superadmin" &&
          c.contextType === "CLUB" &&
          c.status === "ACTIVE"
      );

    if (!isSuperAdmin) {
      return <div style={{ padding: 40, color: "gold" }}>Unauthorized</div>;
    }

  return (
    <div className="cups-scrollpage">

      <h2 className="cups-title">QPSC CUPS – All Time</h2>

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button
          className="home-link-btn"
          onClick={recalculateCups}
          disabled={recalcLoading}
        >
          {recalcLoading ? "Recalculating..." : "🔄 Recalculate QPSC Cups"}
        </button>
      </div>

      {/* FILTRI */}
      <div className="cups-filters">
        <input placeholder="First Name"
          value={searchFirst}
          onChange={e=>setSearchFirst(e.target.value)} />

        <input placeholder="Last Name"
          value={searchLast}
          onChange={e=>setSearchLast(e.target.value)} />

        <select onChange={e=>setGenderFilter(e.target.value)}>
          <option value="">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>

        <select onChange={e=>setSeasonFilter(e.target.value)}>
          <option value="">All Seasons</option>
          {seasons.map(s =>
            <option key={s}>{s}</option>
          )}
        </select>

        <select onChange={e=>setSortBy(e.target.value)}>
          <option value="caps">Caps</option>
          <option value="goals">Goals</option>
          <option value="minutes">Minutes</option>
          <option value="cards">Cards</option>
        </select>
      </div>

      {loading ? <div>Loading...</div> : (
        <div className="cups-card-list">
          {sorted.map((p, idx) => {

            let badgeClass = "";
            if (idx === 0) badgeClass = "gold";
            if (idx === 1) badgeClass = "silver";
            if (idx === 2) badgeClass = "bronze";

            return (
              <div
                key={p.playerId}
                className={`cups-card ${badgeClass}`}
                onClick={()=>navigate(`/qpsc-cups/${p.playerId}`)}
              >
                <div className="cups-rank">#{idx+1}</div>

                <div className="cups-main">
                  <div className="cups-name">
                    {p.firstName} {p.lastName}
                  </div>

                  <div className="cups-dob">
                    DOB: {p.dob || "—"}
                  </div>

                  <div className="cups-appearances">
                    {p.totalAppearances} Caps
                  </div>

                  <div className="cups-stats">
                    ⚽ {p.totalGoals}
                    🕒 {p.totalMinutes}
                    🟨 {p.totalYellowCards}
                    🟥 {p.totalRedCards}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}